import * as React from 'react';
import { getFormattedAmount } from 'frontend-toolkit';
import { PaymentPlanFormValues, PlanType } from './types';
import { MAX_TOTAL_INSTALLMENTS } from '../types';
import { datePresets, getPlanFromFormValues } from './helpers';
import {
  formatPercentage,
  getDateText,
  getPaymentSummary,
} from '../installment-utils';
import { Icon, IconButton, Input, Label, LabelButton, Tooltip } from '@teamsnap/snap-ui';

interface PaymentScheduleProps {
  formValues: PaymentPlanFormValues | null;
  updateField: (field: string, value: any) => void;
  errors?: Record<string, string[]>;
}

interface PaymentScheduleRowProps {
  scheduledDate: React.ReactNode;
  name: string;
  paymentSummary: string;
  onDelete?: () => void;
}

const PaymentSchedulePercentageTooltip = () => (
  <Tooltip
    align="center"
    content="The percentage displayed is rounded to the nearest hundredth; the full order total will always be reflected in the payment plan."
    side="bottom"
  >
    <Icon name="info" className="sui-text-action-icon" />
  </Tooltip>
);

export const PaymentScheduleRow = ({
  scheduledDate,
  name,
  paymentSummary,
  onDelete,
}: PaymentScheduleRowProps) => (
  <tr data-testid="PaymentSchedule--row">
    <td className="sui-py-2">{name}</td>
    <td className="sui-p-2">{scheduledDate}</td>
    <td className="sui-py-2">{paymentSummary}</td>
    <td>
      {onDelete && (
        <IconButton
          icon="delete"
          sentiment="negative"
          onClick={onDelete}
          data-testid="PaymentSchedule--delete"
        />
      )}
    </td>
  </tr>
);

const EditableDate = ({
  scheduledDate,
  onChange,
  index,
  error,
}: {
  error?: string;
  scheduledDate: string;
  index: number;
  onChange: (date: string) => void;
}) => (
  <Input
    type="date"
    name={`scheduledDate-${index}`}
    value={scheduledDate}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{
      min: datePresets.getOneYearAgo(),
      max: '2099-12-31', // to keep the date picker from looping up to around to unreasonable year
    }}
    errors={error ? [error] : []}
  />
);

export const PaymentSchedule = ({
  formValues,
  updateField,
  errors,
}: PaymentScheduleProps) => {
  const plan = getPlanFromFormValues(formValues);
  const depositIsFirstInstallment = plan.depositType === 'first_installment';
  const depositSummary =
    plan.depositType === 'amount'
      ? `${getFormattedAmount(plan.depositAmount / 100)}`
      : `${formatPercentage(plan.depositAmount / 100)}% of total`;

  const addInstallment = () => {
    if (formValues?.scheduledDates?.length === 0) {
      updateField('scheduledDates', [datePresets.getOneMonthFromNow()]);
      return;
    }
    const lastDateIndex = formValues.scheduledDates.length - 1;
    const lastDate = formValues.scheduledDates[lastDateIndex];
    const scheduledDates = [
      ...formValues.scheduledDates,
      datePresets.getOneMonthFromDate(lastDate),
    ];
    updateField('scheduledDates', scheduledDates);
  };

  const getDynamicRowsData = (): PaymentScheduleRowProps[] => {
    const rows: PaymentScheduleRowProps[] = Array.from(
      { length: plan.installmentCount },
      (_, i) => {
        const paymentSummary = getPaymentSummary(plan);
        const scheduledDate = getDateText(
          depositIsFirstInstallment ? i : i + 1,
          plan
        );
        return { name: `Installment ${i + 1}`, paymentSummary, scheduledDate };
      }
    );
    return rows;
  };

  const getCustomRowsData = (): PaymentScheduleRowProps[] => {
    const rows: PaymentScheduleRowProps[] = formValues.scheduledDates.map(
      (scheduledDate, i) => {
        const paymentSummary = getPaymentSummary(plan);
        const onDelete = () => {
          const scheduledDates = formValues.scheduledDates.filter(
            (_, index) => index !== i
          );
          updateField('scheduledDates', scheduledDates);
        };
        const onChange = (date) => {
          const scheduledDates = [...formValues.scheduledDates];
          scheduledDates[i] = date;
          updateField('scheduledDates', scheduledDates);
        };
        const error = errors?.scheduledDates && errors.scheduledDates[i];
        const scheduledDateComponent = (
          <EditableDate {...{ error, scheduledDate, onChange }} index={i} />
        );
        return {
          name: `Installment ${depositIsFirstInstallment ? i + 2 : i + 1}`,
          paymentSummary,
          scheduledDate: scheduledDateComponent,
          onDelete,
        };
      }
    );
    if (depositIsFirstInstallment) {
      rows.unshift({
        name: 'Installment 1',
        paymentSummary: getPaymentSummary(plan),
        scheduledDate: 'Checkout date',
      });
    }
    return rows;
  };

  return (
    <div data-testid="PaymentSchedule--container">
      <div className="sui-flex sui-items-center sui-gap-1 sui-mt-1">
        <Label htmlFor=''>Payment schedule</Label>
        <PaymentSchedulePercentageTooltip />
      </div>
      <div className="PaymentSchedule sui-bg-neutral-background-medium sui-w-full sui-p-2">
        <table
          data-testid="PaymentSchedule--payment-table"
          className="sui-w-full"
        >
          <tbody>
            {!depositIsFirstInstallment && (
              <PaymentScheduleRow
                name="Deposit"
                paymentSummary={
                  plan.depositAmount ? depositSummary : 'Deposit amout not set'
                }
                scheduledDate="Checkout date"
              />
            )}
            {/* {formValues.planType === 'custom' && TODO B2B-4869 } */}
            {formValues.planType === 'dynamic' &&
              plan.installmentCount > 0 &&
              getDynamicRowsData().map((row, i) => (
                <PaymentScheduleRow key={i} {...row} />
              ))}
            {formValues.planType === 'dynamic' &&
              plan.installmentCount === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="sui-py-2 sui-caption"
                    data-testid="PaymentSchedule--no-installments"
                  >
                    Number of installments not set
                  </td>
                </tr>
              )}
            {formValues.planType === 'custom' &&
              formValues.scheduledDates.length > 0 &&
              getCustomRowsData().map((row, i) => (
                <PaymentScheduleRow key={i} {...row} />
              ))}
            {formValues.planType === 'custom' &&
              formValues.scheduledDates.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="sui-py-2 sui-text-negative-text sui-caption"
                    data-testid="PaymentSchedule--no-installments"
                  >
                    {(errors?.scheduledDates && errors?.scheduledDates[0]) ||
                      'No scheduled dates'}
                  </td>
                </tr>
              )}
            {formValues.planType === 'custom' &&
              formValues.scheduledDates.length < MAX_TOTAL_INSTALLMENTS && (
                <tr>
                  <td colSpan={4} className="">
                    <LabelButton
                      icon="add"
                      iconPosition="left"
                      variantType="tertiary"
                      className="sui-btn sui-btn-primary sui-my-1"
                      data-testid="PaymentSchedule--add-installment"
                      labelText="Add installment"
                      onClick={addInstallment}
                    />
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
      {PlanType.Custom === formValues.planType && (
        <div className="sui-body sui-mt-2">
          If date has passed, amount will be collected at checkout.
        </div>
      )}
    </div>
  );
};
